// components/Resume/SectionManager.js
import React, { useState, useEffect } from 'react';
import { BiUpArrow, BiDownArrow, BiMove, BiLeftArrow,  BiRightArrow } from 'react-icons/bi';
import { MdSettings } from 'react-icons/md';
import './SectionManager.css';

const SectionManager = ({ 
  children, 
  sectionKey, 
  sections, 
  resumeData, 
  isSingleLayout, 
  updateResumeData, 
  onSectionClick 
}) => {
  const [showSectionSettings, setShowSectionSettings] = useState({});
  const [hoveredSection, setHoveredSection] = useState(null);

  // Ensure isSingleLayout is always a boolean (default to false if undefined)
  const isActuallySingleLayout = isSingleLayout === undefined ? false : isSingleLayout;

  // Close settings when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.section-wrapper')) {
        setShowSectionSettings({});
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const section = sections[sectionKey];
  
  // Don't render wrapper for personal details or if section doesn't exist
  if (!section || !section.isDisplay || !section.isShow || sectionKey === 'personalDetails') {
    return children;
  }

  const toggleSectionSettings = (key) => {
    setShowSectionSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const moveSectionUp = (key) => {
    // Always check if resumeData and sectionOrder exist
    if (!resumeData?.sectionOrder) {
      console.warn('Cannot move section: resumeData.sectionOrder is missing');
      return;
    }
    
    // Helper function to check if a section should be displayed
    const isSectionDisplayed = (sectionKey) => {
      return sections[sectionKey]?.isDisplay === true && sections[sectionKey]?.isShow === true;
    };
    
    if (isActuallySingleLayout) {
      // For single column layout, work with singleColumn array
      const currentOrder = resumeData.sectionOrder.singleColumn ? [...resumeData.sectionOrder.singleColumn] : [];
      
      // Filter to get only displayed sections in their current order
      const displayedSections = currentOrder.filter(sectionKey => isSectionDisplayed(sectionKey));
      const currentDisplayIndex = displayedSections.indexOf(key);
      
      console.log('Displayed sections:', displayedSections);
      console.log('Current section in displayed list:', currentDisplayIndex);
      
      if (currentDisplayIndex <= 0) {
        console.warn('Cannot move section up: already at top of displayed sections or section not found');
        return; // Can't move up if already at top of displayed sections
      }
      
      // Get the section to swap with (previous displayed section)
      const targetSection = displayedSections[currentDisplayIndex - 1];
      
      // Find their actual positions in the full order
      const currentActualIndex = currentOrder.indexOf(key);
      const targetActualIndex = currentOrder.indexOf(targetSection);
      
      console.log(`Swapping ${key} (index ${currentActualIndex}) with ${targetSection} (index ${targetActualIndex})`);
      
      // Swap their positions in the actual order
      [currentOrder[currentActualIndex], currentOrder[targetActualIndex]] = 
      [currentOrder[targetActualIndex], currentOrder[currentActualIndex]];
      
      updateSectionOrder(currentOrder);
    } else {
      // For two-column layout, we need to handle left and right columns separately
      if (!resumeData.sectionOrder.twoColumns) {
        console.warn('Cannot move section: twoColumns structure is missing');
        return;
      }

      const { leftColumn, rightColumn } = resumeData.sectionOrder.twoColumns;
      const isInLeftColumn = leftColumn?.includes(key);
      
      if (isInLeftColumn) {
        const newLeftColumn = [...(leftColumn || [])];
        
        // Filter to get only displayed sections in left column
        const displayedLeftSections = newLeftColumn.filter(sectionKey => isSectionDisplayed(sectionKey));
        const currentDisplayIndex = displayedLeftSections.indexOf(key);
        
        console.log('Displayed left column sections:', displayedLeftSections);
        console.log('Current section in displayed list:', currentDisplayIndex);
        
        if (currentDisplayIndex <= 0) {
          console.warn('Cannot move section up: already at top of displayed sections in left column');
          return; // Can't move up if already at top of displayed sections
        }
        
        // Get the section to swap with (previous displayed section)
        const targetSection = displayedLeftSections[currentDisplayIndex - 1];
        
        // Find their actual positions in the full left column
        const currentActualIndex = newLeftColumn.indexOf(key);
        const targetActualIndex = newLeftColumn.indexOf(targetSection);
        
        console.log(`Swapping ${key} (index ${currentActualIndex}) with ${targetSection} (index ${targetActualIndex}) in left column`);
        
        // Swap their positions in the actual left column
        [newLeftColumn[currentActualIndex], newLeftColumn[targetActualIndex]] = 
        [newLeftColumn[targetActualIndex], newLeftColumn[currentActualIndex]];
        
        const newSectionOrder = {
          ...resumeData.sectionOrder,
          twoColumns: {
            leftColumn: newLeftColumn,
            rightColumn: [...(rightColumn || [])]
          }
        };
        
        // Update formOrder to reflect new arrangement
        const newFormOrder = [...newLeftColumn, ...(rightColumn || [])];
        
        updateResumeData({
          ...resumeData,
          sectionOrder: newSectionOrder,
          formOrder: newFormOrder
        });
      } else if (rightColumn?.includes(key)) {
        const newRightColumn = [...rightColumn];
        
        // Filter to get only displayed sections in right column
        const displayedRightSections = newRightColumn.filter(sectionKey => isSectionDisplayed(sectionKey));
        const currentDisplayIndex = displayedRightSections.indexOf(key);
        
        console.log('Displayed right column sections:', displayedRightSections);
        console.log('Current section in displayed list:', currentDisplayIndex);
        
        if (currentDisplayIndex <= 0) {
          console.warn('Cannot move section up: already at top of displayed sections in right column');
          return; // Can't move up if already at top of displayed sections
        }
        
        // Get the section to swap with (previous displayed section)
        const targetSection = displayedRightSections[currentDisplayIndex - 1];
        
        // Find their actual positions in the full right column
        const currentActualIndex = newRightColumn.indexOf(key);
        const targetActualIndex = newRightColumn.indexOf(targetSection);
        
        console.log(`Swapping ${key} (index ${currentActualIndex}) with ${targetSection} (index ${targetActualIndex}) in right column`);
        
        // Swap their positions in the actual right column
        [newRightColumn[currentActualIndex], newRightColumn[targetActualIndex]] = 
        [newRightColumn[targetActualIndex], newRightColumn[currentActualIndex]];
        
        const newSectionOrder = {
          ...resumeData.sectionOrder,
          twoColumns: {
            leftColumn: [...(leftColumn || [])],
            rightColumn: newRightColumn
          }
        };
        
        // Update formOrder to reflect new arrangement
        const newFormOrder = [...(leftColumn || []), ...newRightColumn];
        
        updateResumeData({
          ...resumeData,
          sectionOrder: newSectionOrder,
          formOrder: newFormOrder
        });
      } else {
        console.warn('Section not found in either column');
      }
    }
  };

  const moveSectionDown = (key) => {
    // Always check if resumeData and sectionOrder exist
    if (!resumeData?.sectionOrder) {
      console.warn('Cannot move section: resumeData.sectionOrder is missing');
      return;
    }
    
    // Helper function to check if a section should be displayed
    const isSectionDisplayed = (sectionKey) => {
      return sections[sectionKey]?.isDisplay === true && sections[sectionKey]?.isShow === true;
    };
    
    if (isActuallySingleLayout) {
      // For single column layout, work with singleColumn array
      const currentOrder = resumeData.sectionOrder.singleColumn ? [...resumeData.sectionOrder.singleColumn] : [];
      
      // Filter to get only displayed sections in their current order
      const displayedSections = currentOrder.filter(sectionKey => isSectionDisplayed(sectionKey));
      const currentDisplayIndex = displayedSections.indexOf(key);
      
      console.log('Displayed sections:', displayedSections);
      console.log('Current section in displayed list:', currentDisplayIndex);
      
      if (currentDisplayIndex >= displayedSections.length - 1 || currentDisplayIndex === -1) {
        console.warn('Cannot move section down: already at bottom of displayed sections or section not found');
        return; // Can't move down if already at bottom of displayed sections
      }
      
      // Get the section to swap with (next displayed section)
      const targetSection = displayedSections[currentDisplayIndex + 1];
      
      // Find their actual positions in the full order
      const currentActualIndex = currentOrder.indexOf(key);
      const targetActualIndex = currentOrder.indexOf(targetSection);
      
      console.log(`Swapping ${key} (index ${currentActualIndex}) with ${targetSection} (index ${targetActualIndex})`);
      
      // Swap their positions in the actual order
      [currentOrder[currentActualIndex], currentOrder[targetActualIndex]] = 
      [currentOrder[targetActualIndex], currentOrder[currentActualIndex]];
      
      updateSectionOrder(currentOrder);
    } else {
      // For two-column layout, we need to handle left and right columns separately
      if (!resumeData.sectionOrder.twoColumns) {
        console.warn('Cannot move section: twoColumns structure is missing');
        return;
      }

      const { leftColumn, rightColumn } = resumeData.sectionOrder.twoColumns;
      const isInLeftColumn = leftColumn?.includes(key);
      
      if (isInLeftColumn) {
        const newLeftColumn = [...(leftColumn || [])];
        
        // Filter to get only displayed sections in left column
        const displayedLeftSections = newLeftColumn.filter(sectionKey => isSectionDisplayed(sectionKey));
        const currentDisplayIndex = displayedLeftSections.indexOf(key);
        
        console.log('Displayed left column sections:', displayedLeftSections);
        console.log('Current section in displayed list:', currentDisplayIndex);
        
        if (currentDisplayIndex >= displayedLeftSections.length - 1) {
          console.warn('Cannot move section down: already at bottom of displayed sections in left column');
          return; // Can't move down if already at bottom of displayed sections
        }
        
        // Get the section to swap with (next displayed section)
        const targetSection = displayedLeftSections[currentDisplayIndex + 1];
        
        // Find their actual positions in the full left column
        const currentActualIndex = newLeftColumn.indexOf(key);
        const targetActualIndex = newLeftColumn.indexOf(targetSection);
        
        console.log(`Swapping ${key} (index ${currentActualIndex}) with ${targetSection} (index ${targetActualIndex}) in left column`);
        
        // Swap their positions in the actual left column
        [newLeftColumn[currentActualIndex], newLeftColumn[targetActualIndex]] = 
        [newLeftColumn[targetActualIndex], newLeftColumn[currentActualIndex]];
        
        const newSectionOrder = {
          ...resumeData.sectionOrder,
          twoColumns: {
            leftColumn: newLeftColumn,
            rightColumn: [...(rightColumn || [])]
          }
        };
        
        // Update formOrder to reflect new arrangement
        const newFormOrder = [...newLeftColumn, ...(rightColumn || [])];
        
        updateResumeData({
          ...resumeData,
          sectionOrder: newSectionOrder,
          formOrder: newFormOrder
        });
      } else if (rightColumn?.includes(key)) {
        const newRightColumn = [...rightColumn];
        
        // Filter to get only displayed sections in right column
        const displayedRightSections = newRightColumn.filter(sectionKey => isSectionDisplayed(sectionKey));
        const currentDisplayIndex = displayedRightSections.indexOf(key);
        
        console.log('Displayed right column sections:', displayedRightSections);
        console.log('Current section in displayed list:', currentDisplayIndex);
        
        if (currentDisplayIndex >= displayedRightSections.length - 1) {
          console.warn('Cannot move section down: already at bottom of displayed sections in right column');
          return; // Can't move down if already at bottom of displayed sections
        }
        
        // Get the section to swap with (next displayed section)
        const targetSection = displayedRightSections[currentDisplayIndex + 1];
        
        // Find their actual positions in the full right column
        const currentActualIndex = newRightColumn.indexOf(key);
        const targetActualIndex = newRightColumn.indexOf(targetSection);
        
        console.log(`Swapping ${key} (index ${currentActualIndex}) with ${targetSection} (index ${targetActualIndex}) in right column`);
        
        // Swap their positions in the actual right column
        [newRightColumn[currentActualIndex], newRightColumn[targetActualIndex]] = 
        [newRightColumn[targetActualIndex], newRightColumn[currentActualIndex]];
        
        const newSectionOrder = {
          ...resumeData.sectionOrder,
          twoColumns: {
            leftColumn: [...(leftColumn || [])],
            rightColumn: newRightColumn
          }
        };
        
        // Update formOrder to reflect new arrangement
        const newFormOrder = [...(leftColumn || []), ...newRightColumn];
        
        updateResumeData({
          ...resumeData,
          sectionOrder: newSectionOrder,
          formOrder: newFormOrder
        });
      } else {
        console.warn('Section not found in either column');
      }
    }
  };

  const moveSectionToOtherColumn = (key) => {
    if (isActuallySingleLayout || !resumeData?.sectionOrder?.twoColumns) {
      console.warn('Cannot move to other column: single layout or twoColumns structure missing');
      return;
    }
    
    const { leftColumn, rightColumn } = resumeData.sectionOrder.twoColumns;
    const isInLeftColumn = leftColumn?.includes(key);
    
    const newLeftColumn = isInLeftColumn 
      ? (leftColumn || []).filter(id => id !== key)
      : [...(leftColumn || []), key];
      
    const newRightColumn = isInLeftColumn 
      ? [...(rightColumn || []), key]
      : (rightColumn || []).filter(id => id !== key);
    
    const newSectionOrder = {
      ...resumeData.sectionOrder,
      twoColumns: {
        leftColumn: newLeftColumn,
        rightColumn: newRightColumn
      }
    };
    
    updateResumeData({
      ...resumeData,
      sectionOrder: newSectionOrder
    });
  };

  const updateSectionOrder = (newOrder) => {
    if (isActuallySingleLayout) {
      const newSectionOrder = {
        ...resumeData.sectionOrder,
        singleColumn: newOrder
      };
      
      updateResumeData({
        ...resumeData,
        sectionOrder: newSectionOrder,
        formOrder: newOrder
      });
    } else {
      // This function is only called for single column now
      // Two-column updates are handled directly in moveSectionUp/Down
      console.warn('updateSectionOrder called for two-column layout');
    }
  };

  return (
    <div 
      className={`section-wrapper ${hoveredSection === sectionKey ? 'hovered' : ''}`}
      onMouseEnter={() => setHoveredSection(sectionKey)}
      onMouseLeave={() => setHoveredSection(null)}
    >
      {/* Settings Button */}
      {(hoveredSection === sectionKey || showSectionSettings[sectionKey]) && (
        <div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSectionSettings(sectionKey);
            }}
            className="section-settings-button"
            title="Section Settings"
          >
            <MdSettings className="settings-icon" />
          </button>
        </div>
      )}

      {/* Settings Modal */}
      {showSectionSettings[sectionKey] && (
        <div 
          className="section-settings-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="section-settings-header">
            Adjust Section
          </div>
          
          <div className="section-settings-buttons">
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('Moving section up:', sectionKey);
                console.log('Current section order:', resumeData.sectionOrder);
                console.log('Is single layout:', isActuallySingleLayout);
                moveSectionUp(sectionKey);
                toggleSectionSettings(sectionKey);
              }}
              className="section-action-button"
              title="Move section up in the order"
            >
              <BiUpArrow className="action-icon" />
              <span>Move Up</span>
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('Moving section down:', sectionKey);
                console.log('Current section order:', resumeData.sectionOrder);
                console.log('Is single layout:', isActuallySingleLayout);
                moveSectionDown(sectionKey);
                toggleSectionSettings(sectionKey);
              }}
              className="section-action-button"
              title="Move section down in the order"
            >
              <BiDownArrow className="action-icon" />
              <span>Move Down</span>
            </button>

            {!isActuallySingleLayout && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  moveSectionToOtherColumn(sectionKey);
                  toggleSectionSettings(sectionKey);
                }}
                className="section-action-button"
                title="Move section to the other column"
              >
                <span className='column-icon'>
                    <span className='left-arrow'><BiLeftArrow className="action-icon" />  <span>Move Left Column</span></span>
                    <span className='right-arrow'><BiRightArrow className="action-icon" />  <span>Move Right Column</span></span>
              </span>
               
              </button>
            )}
          </div>

          <div className="section-settings-footer">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSectionSettings(sectionKey);
              }}
              className="section-close-button"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {children}
    </div>
  );
};

export default SectionManager;